import React from "react";

/** Callers that pass no tags — or a list whose rows carry none — key every set size. */
const MULTIPLE_BIRTH_TAGS_FALLBACK = ["Twin", "Trip", "Quad"];

/**
 * Footer legend explaining the markers that appear next to names in a list.
 *
 * Renders: Legend  *In-Laws  •  <angel>Deceased  •  <Twin Trip>Multiple Births
 *
 * The angel and the multiple-birth tag are superscripted here exactly as they are
 * beside the names, so the legend reads as a key to the rows above it.
 *
 * The entries always come in this order — In-Laws, Deceased, Multiple Births —
 * with the Family Lines colour key (start/top of line, then oldest to youngest)
 * following on the pages that carry one. The key is a key to the markers the
 * page can print, not to the ones it happens to hold on the day, so it reads
 * identically on a page however the user sorts or filters it.
 *
 * `multipleBirthTags` still narrows that entry's tags to the set sizes a list
 * carries — a page of twins alone reads "Twin" — and falls back to all three when
 * the list carries none, so the entry itself never drops out.
 *
 * `showInLaws` drops that entry — off on the two milestone-ages reports,
 * which asked for the shorter key.
 *
 * `showDeceased` drops the angel entry — off on Phone Number Reports, which
 * lists the living only.
 */
export default function DashboardMarkerLegend({
  multipleBirthTags = MULTIPLE_BIRTH_TAGS_FALLBACK,
  showInLaws = true,
  showDeceased = true,
}) {
  // No entry for the Start/Top of Line star: on the pages that carry a colour
  // legend it already names those people under "Family Lines", and the rest name
  // the head of the line on the row itself.
  const items = [
    showInLaws && (
      <span className="in-law-footer-text" key="in-laws">
        <span className="in-law-footer-asterisk">*</span>
        <span>In-Laws</span>
      </span>
    ),

    showDeceased && (
      <span
        className="in-law-footer-text dashboard-marker-legend-item--deceased"
        key="deceased"
      >
        <sup className="dashboard-marker-legend-sup">
          <img
            className="dashboard-marker-legend-icon"
            src={window.getPublicUrl("/assets/images/angel.png")}
            alt=""
            aria-hidden="true"
          />
        </sup>
        <span>Deceased</span>
      </span>
    ),

    // Keyed to the tags on screen where there are any: a list with only twins
    // reads "Twin", one with twins and triplets reads "Twin Trip".
    <span className="in-law-footer-text" key="multiple-birth">
      <sup className="dashboard-multiple-birth-sup">
        {(multipleBirthTags?.length
          ? multipleBirthTags
          : MULTIPLE_BIRTH_TAGS_FALLBACK
        ).join(" ")}
      </sup>
      <span>Multiple Births</span>
    </span>,
  ].filter(Boolean);

  // The "Legend" label heads the whole footer — including the colour legend that
  // follows it on the pages that carry one.
  return (
    <>
      <span className="dashboard-marker-legend-title">Legend</span>
      {items.map((item, index) => (
        <React.Fragment key={item.key}>
          {index > 0 && (
            <span className="dashboard-marker-legend-separator" aria-hidden="true">
              •
            </span>
          )}
          {item}
        </React.Fragment>
      ))}
    </>
  );
}
